/* SEREIN QUEEN — RESPONSIVE UNIFIED MENU BELT V7
   Lightweight geometry/control owner. Normal dropdowns show six complete cards;
   More shows five complete cards in its narrower right rail. Native overflow-list
   remains the authority for moving top-level items into More and restoring them
   in original order when browser width returns. */
(() => {
  'use strict';
  if (window.__SQ_UNIFIED_MENU_BELT_V7__) return;
  window.__SQ_UNIFIED_MENU_BELT_V7__ = true;
  window.__SQ_REGULAR_MENU_SIX_CARD_V6__ = true;
  window.__sqMoreMenuCleanupV5 = true;

  const DESKTOP = matchMedia('(min-width:990px) and (hover:hover) and (pointer:fine)');
  const HEADER = '#header-component';
  const PREVIEW = `${HEADER} header-menu .sq-menu-product-preview:not(.sq-menu-product-preview--mobile)`;
  const VIEWPORT = ':is([data-sq-menu-preview-viewport],.sq-menu-product-preview__viewport)';
  const CARD = ':is([data-sq-menu-product-card],.sq-menu-product-card)';
  const GAP = 16;
  const REGULAR_VISIBLE = 6;
  const MORE_VISIBLE = 5;
  const bound = new WeakSet();
  let resizeFrame = 0;

  const normalize = value => String(value || '').replace(/\s+/g,' ').trim().toLowerCase();

  function cleanLegacyHairAccessory(root=document){
    root.querySelectorAll?.(`${HEADER} a[href]`).forEach(anchor => {
      let remove = normalize(anchor.textContent) === 'hair accessories';
      if (!remove) {
        try {
          const url = new URL(anchor.href,location.origin);
          remove = url.pathname.replace(/\/$/,'') === '/search' && normalize(url.searchParams.get('q')) === 'hair accessory';
        } catch (_) {}
      }
      if (!remove) return;
      const item = anchor.closest('li,.menu-list__list-item,.menu-drawer__list-item');
      (item || anchor).remove();
    });
  }

  function isMorePreview(preview){
    return Boolean(preview?.closest?.('.menu-list__list-item[slot="overflow"]'));
  }

  function visibleCount(preview){
    return isMorePreview(preview) ? MORE_VISIBLE : REGULAR_VISIBLE;
  }

  function geometry(preview){
    if (!(preview instanceof HTMLElement) || !DESKTOP.matches) return;
    const viewport = preview.querySelector(VIEWPORT);
    if (!(viewport instanceof HTMLElement)) return;
    const width = viewport.clientWidth || viewport.getBoundingClientRect().width;
    if (width <= 0) return;
    const visible = visibleCount(preview);
    const cardWidth = Math.max(118,(width - GAP * (visible - 1)) / visible);
    preview.style.setProperty('--sq-menu-runtime-card-width',`${cardWidth}px`);
    preview.style.setProperty('--sq-menu-belt-card-width',`${cardWidth}px`);
    updateArrows(preview);
  }

  function step(preview){
    const card = preview.querySelector(CARD);
    return card instanceof HTMLElement ? card.getBoundingClientRect().width + GAP : 0;
  }

  function updateArrows(preview){
    const viewport = preview.querySelector(VIEWPORT);
    if (!(viewport instanceof HTMLElement)) return;
    const max = Math.max(0,viewport.scrollWidth - viewport.clientWidth);
    const prev = preview.querySelector('[data-sq-menu-belt-prev]');
    const next = preview.querySelector('[data-sq-menu-belt-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = viewport.scrollLeft <= 2;
    if (next instanceof HTMLButtonElement) next.disabled = max <= 2 || viewport.scrollLeft >= max - 2;
  }

  function ensureControls(preview){
    if (!(preview instanceof HTMLElement) || !DESKTOP.matches) return;
    const heading = preview.querySelector('.sq-menu-product-preview__heading');
    const viewport = preview.querySelector(VIEWPORT);
    if (!(heading instanceof HTMLElement) || !(viewport instanceof HTMLElement)) return;

    let controls = heading.querySelector('.sq-menu-product-preview__controls');
    if (!(controls instanceof HTMLElement)) {
      controls = document.createElement('div');
      controls.className = 'sq-menu-product-preview__controls';
      const prev = document.createElement('button');
      const next = document.createElement('button');
      prev.type = next.type = 'button';
      prev.className = next.className = 'sq-menu-product-preview__arrow';
      prev.dataset.sqMenuBeltPrev = '';
      next.dataset.sqMenuBeltNext = '';
      prev.setAttribute('aria-label','Previous products');
      next.setAttribute('aria-label','Next products');
      prev.textContent = '‹';
      next.textContent = '›';
      controls.append(prev,next);
      heading.appendChild(controls);

      const move = (direction,event) => {
        event.preventDefault();
        event.stopPropagation();
        const amount = step(preview);
        if (!amount) return;
        const max = Math.max(0,viewport.scrollWidth - viewport.clientWidth);
        viewport.scrollLeft = Math.max(0,Math.min(max,viewport.scrollLeft + direction * amount));
        updateArrows(preview);
      };
      prev.addEventListener('click',event => move(-1,event));
      next.addEventListener('click',event => move(1,event));
    }

    if (!bound.has(viewport)) {
      bound.add(viewport);
      viewport.addEventListener('scroll',() => updateArrows(preview),{passive:true});
    }
    geometry(preview);
  }

  function activateOverflowItem(header,item){
    if (!(header instanceof HTMLElement) || !(item instanceof HTMLElement)) return;
    header.querySelectorAll('header-menu .menu-list__list-item.sq-more-active').forEach(other => {
      if (other === item) return;
      other.classList.remove('sq-more-active','sq-favorites-more-active');
      other.querySelector(':scope > .menu-list__link')?.setAttribute('aria-expanded','false');
      other.querySelector(':scope > .menu-list__submenu')?.removeAttribute('data-active');
    });
    item.classList.add('sq-more-active');
    const submenu = item.querySelector(':scope > .menu-list__submenu');
    const link = item.querySelector(':scope > .menu-list__link');
    link?.setAttribute('aria-expanded',submenu ? 'true' : 'false');
    if (submenu) submenu.setAttribute('data-active','');
  }

  function syncResponsiveMore(header){
    if (!(header instanceof HTMLElement)) return;
    const items = [...header.querySelectorAll('header-menu .menu-list__list-item[slot="overflow"]')];

    /* Shopify's native overflow-list has already preserved DOM order here.
       We only reconcile the currently active More row after a resize. */
    if (!header.classList.contains('sq-more-hold-open')) return;

    if (!items.length) {
      document.dispatchEvent(new CustomEvent('sq:more-menu-close-request',{detail:{header,source:'sq-overflow-empty-after-resize'}}));
      document.dispatchEvent(new CustomEvent('sq:header-menu-force-close',{detail:{header,source:'sq-overflow-empty-after-resize'}}));
      header.classList.remove('sq-more-hold-open');
      return;
    }

    const current = items.find(item => item.classList.contains('sq-more-active'));
    if (!current) activateOverflowItem(header,items[0]);
  }

  function scan(root=document){
    if (!DESKTOP.matches) return;
    const previews = [];
    if (root instanceof Element && root.matches?.(PREVIEW)) previews.push(root);
    root.querySelectorAll?.(PREVIEW).forEach(node => previews.push(node));
    [...new Set(previews)].forEach(ensureControls);
    const header = document.querySelector(HEADER);
    if (header instanceof HTMLElement) syncResponsiveMore(header);
  }

  function start(){
    cleanLegacyHairAccessory();
    scan();
    const header = document.querySelector(HEADER);
    if (!(header instanceof HTMLElement)) return;

    new MutationObserver(records => {
      let relevant = false;
      for (const record of records) {
        if (record.type === 'attributes' && record.attributeName === 'slot') {
          relevant = true;
          continue;
        }
        for (const node of record.addedNodes) {
          if (!(node instanceof Element)) continue;
          cleanLegacyHairAccessory(node);
          if (node.matches?.('.sq-menu-product-preview,.sq-menu-product-card') || node.querySelector?.('.sq-menu-product-preview,.sq-menu-product-card')) relevant = true;
        }
      }
      if (relevant) requestAnimationFrame(() => scan(header));
    }).observe(header,{childList:true,subtree:true,attributes:true,attributeFilter:['slot']});
  }

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded',start,{once:true}) : start();
  document.addEventListener('shopify:section:load',event => requestAnimationFrame(() => scan(event.target)));
  document.addEventListener('sq:more-launch-ready',event => requestAnimationFrame(() => scan(event.detail?.header || document)));

  window.addEventListener('resize',() => {
    if (resizeFrame) cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      scan();
    });
  },{passive:true});
})();
